// PL-S4: shared zoom / timeline-tier utilities.
// Extracted from GanttView.js so both the single-project Gantt and the
// portfolio Gantt share one tier engine.
//
// Two tier ladders intentionally coexist (PO decision, PL-S4):
//   - getTiers          — 5-level, used by GanttView (single project, day-level)
//   - getPortfolioTiers — 6-level incl. Half-Year, used by PortfolioGanttView
// GanttView's behaviour and its existing zoom tests are unaffected.

export const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// ISO week number (Mon-start)
export function isoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// ── Tier ladders ──────────────────────────────────────────────────────

// Single-project Gantt (GanttView) — 5 levels. Unchanged from G-S4.
export function getTiers(pxPerDay) {
  if (pxPerDay < 3) return { top: 'year', middle: 'quarter' };
  if (pxPerDay < 8) return { top: 'quarter', middle: 'month' };
  if (pxPerDay < 20) return { top: 'month', middle: 'week' };
  if (pxPerDay < 50) return { top: 'week', middle: 'day' };
  return { top: 'day', middle: 'hour' };
}

export function getZoomLabel(pxPerDay) {
  const tiers = getTiers(pxPerDay);
  return tiers.top.charAt(0).toUpperCase() + tiers.top.slice(1);
}

// Portfolio Gantt — 6 levels, adds Year-over-Half-Year and Half-Year-over-Quarter.
export function getPortfolioTiers(pxPerDay) {
  if (pxPerDay < 1.5) return { top: 'year', middle: 'halfyear' };
  if (pxPerDay < 3) return { top: 'halfyear', middle: 'quarter' };
  if (pxPerDay < 8) return { top: 'quarter', middle: 'month' };
  if (pxPerDay < 20) return { top: 'month', middle: 'week' };
  if (pxPerDay < 50) return { top: 'week', middle: 'day' };
  return { top: 'day', middle: 'hour' };
}

const TIER_LABEL = {
  year: 'Year', halfyear: 'Half-Year', quarter: 'Quarter',
  month: 'Month', week: 'Week', day: 'Day', hour: 'Hour',
};
export function getPortfolioZoomLabel(pxPerDay) {
  return TIER_LABEL[getPortfolioTiers(pxPerDay).top] || '';
}

// ── Presets ───────────────────────────────────────────────────────────
export const PORTFOLIO_PRESETS = [
  { label: 'Year', px: 0.5 },
  { label: 'Half-Year', px: 1.5 },
  { label: 'Quarter', px: 4 },
  { label: 'Month', px: 12 },
];

export const PROJECT_DETAIL_PRESETS = [
  { label: 'Year', px: 2 },
  { label: 'Quarter', px: 5 },
  { label: 'Month', px: 12 },
  { label: 'Week', px: 35 },
  { label: 'Day', px: 100 },
];

// ── Tier segment generation ───────────────────────────────────────────

// Iterate unit boundaries covering [windowStart, windowEnd). Returns {start,end} pairs.
export function unitBoundaries(unit, windowStart, windowEnd) {
  const segs = [];
  let cur;

  switch (unit) {
    case 'year':
      cur = new Date(windowStart.getFullYear(), 0, 1);
      while (cur < windowEnd) {
        const next = new Date(cur.getFullYear() + 1, 0, 1);
        segs.push({ start: cur, end: next });
        cur = next;
      }
      break;
    case 'halfyear': {
      // H1 = Jan–Jun, H2 = Jul–Dec
      const m0 = windowStart.getMonth() < 6 ? 0 : 6;
      cur = new Date(windowStart.getFullYear(), m0, 1);
      while (cur < windowEnd) {
        const isH1 = cur.getMonth() === 0;
        const next = isH1
          ? new Date(cur.getFullYear(), 6, 1)
          : new Date(cur.getFullYear() + 1, 0, 1);
        segs.push({ start: cur, end: next });
        cur = next;
      }
      break;
    }
    case 'quarter':
      cur = new Date(windowStart.getFullYear(), Math.floor(windowStart.getMonth() / 3) * 3, 1);
      while (cur < windowEnd) {
        const next = new Date(cur.getFullYear(), cur.getMonth() + 3, 1);
        segs.push({ start: cur, end: next });
        cur = next;
      }
      break;
    case 'month':
      cur = new Date(windowStart.getFullYear(), windowStart.getMonth(), 1);
      while (cur < windowEnd) {
        const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        segs.push({ start: cur, end: next });
        cur = next;
      }
      break;
    case 'week': {
      cur = new Date(windowStart);
      cur.setHours(0, 0, 0, 0);
      const dow = cur.getDay() || 7;
      cur.setDate(cur.getDate() - (dow - 1));
      while (cur < windowEnd) {
        const next = new Date(cur);
        next.setDate(next.getDate() + 7);
        segs.push({ start: new Date(cur), end: next });
        cur = next;
      }
      break;
    }
    case 'day':
      cur = new Date(windowStart);
      cur.setHours(0, 0, 0, 0);
      while (cur < windowEnd) {
        const next = new Date(cur);
        next.setDate(next.getDate() + 1);
        segs.push({ start: new Date(cur), end: next });
        cur = next;
      }
      break;
    case 'hour':
      cur = new Date(windowStart);
      cur.setMinutes(0, 0, 0);
      while (cur < windowEnd) {
        const next = new Date(cur);
        next.setHours(next.getHours() + 1);
        segs.push({ start: new Date(cur), end: next });
        cur = next;
      }
      break;
    default:
      break;
  }

  return segs;
}

export function topTierLabel(unit, d) {
  switch (unit) {
    case 'year':     return String(d.getFullYear());
    case 'halfyear': return `${d.getMonth() < 6 ? 'H1' : 'H2'} ${d.getFullYear()}`;
    case 'quarter':  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    case 'month':    return `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    case 'week':     return `W${isoWeek(d)} ${SHORT_MONTHS[d.getMonth()]}`;
    case 'day':      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    default:         return '';
  }
}

export function middleTierLabel(unit, d) {
  switch (unit) {
    case 'halfyear': return d.getMonth() < 6 ? 'H1' : 'H2';
    case 'quarter':  return `Q${Math.floor(d.getMonth() / 3) + 1}`;
    case 'month':    return SHORT_MONTHS[d.getMonth()];
    case 'week':     return `W${isoWeek(d)}`;
    case 'day':      return String(d.getDate());
    case 'hour':     return String(d.getHours()).padStart(2, '0');
    default:         return '';
  }
}

// Half-year segments with pixel positions — convenience for the portfolio view.
// H1 = Jan–Jun, H2 = Jul–Dec.
export function getHalfYearSegments(startDate, endDate, dayToX) {
  const out = [];
  let cur = new Date(startDate.getFullYear(), startDate.getMonth() < 6 ? 0 : 6, 1);
  while (cur < endDate) {
    const isH1 = cur.getMonth() === 0;
    const next = isH1
      ? new Date(cur.getFullYear(), 6, 1)
      : new Date(cur.getFullYear() + 1, 0, 1);
    const segStart = cur < startDate ? startDate : cur;
    const segEnd = next > endDate ? endDate : next;
    out.push({
      label: `${isH1 ? 'H1' : 'H2'} ${cur.getFullYear()}`,
      x: dayToX(segStart),
      width: dayToX(segEnd) - dayToX(segStart),
    });
    cur = next;
  }
  return out;
}
